/**
 * Thin wrapper around Clerk's `useUser` that honours a dev/test-only
 * session-storage override. Used by the onboarding wizard so Playwright
 * can drive the UI without a real Clerk session.
 *
 * Safety:
 *   - The entire test-mode branch is wrapped in
 *     `import.meta.env.DEV && import.meta.env.VITE_FRAGMENTS_TEST_MODE === '1'`.
 *     Both values resolve at build time, so a production bundle inlines
 *     `false` and Vite dead-code-eliminates the override. Grep the prod
 *     build for TEST_CLERK_USER_STORAGE_KEY to confirm: it must not appear.
 *   - The override is additive: when no session-storage value is set,
 *     behavior is identical to `useUser()`.
 *
 * Sister helper: `e2e/onboarding/helpers/auth-fixture.ts` seeds the
 * session-storage key before navigation.
 */
import * as React from 'react';
import { useUser } from '@clerk/tanstack-react-start';

export const TEST_CLERK_USER_STORAGE_KEY = '__fragments_test_clerk_user__';
export const TEST_CLERK_USER_CHANGED_EVENT = 'fragments:test-clerk-user';

const TEST_MODE_ACTIVE =
  import.meta.env.DEV && import.meta.env.VITE_FRAGMENTS_TEST_MODE === '1';

function isLocalDevFixturePage(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

export interface ClerkUserLike {
  user:
    | {
        id: string;
        fullName?: string | null;
        primaryEmailAddress?: { emailAddress?: string | null } | null;
        imageUrl?: string | null;
      }
    | null
    | undefined;
  isLoaded: boolean;
}

function readTestClerkUserId(): string | null {
  if (typeof window === 'undefined') return null;
  if (!TEST_MODE_ACTIVE && !isLocalDevFixturePage()) return null;
  try {
    const searchValue = new URLSearchParams(window.location.search).get(
      '__test_user',
    );
    if (searchValue && searchValue.trim().length > 0) return searchValue;
    const value =
      window.sessionStorage.getItem(TEST_CLERK_USER_STORAGE_KEY) ??
      window.localStorage.getItem(TEST_CLERK_USER_STORAGE_KEY);
    return value && value.trim().length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function useClerkUser(): ClerkUserLike {
  const clerk = useUser();
  const [testUserId, setTestUserId] = React.useState(readTestClerkUserId);

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;

    const update = () => setTestUserId(readTestClerkUserId());
    update();
    const interval = window.setInterval(update, 100);
    window.addEventListener(TEST_CLERK_USER_CHANGED_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(TEST_CLERK_USER_CHANGED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  // The fixture writes the sessionStorage key via addInitScript before
  // any page script runs, so the value is stable for the page's lifetime.
  // Reading on every render is cheap and avoids a stale-state window —
  // same-tab sessionStorage writes do not fire `storage` events, so a
  // useState+listener would not observe mid-test swaps anyway.
  const currentTestUserId = testUserId ?? readTestClerkUserId();
  if (currentTestUserId) {
    return { user: { id: currentTestUserId }, isLoaded: true };
  }
  return { user: clerk.user ?? null, isLoaded: clerk.isLoaded };
}
